import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, ReconLabSDKConfig } from './types';

/**
 * ReconLab API Client
 */
export class ReconLabClient {
  private client: AxiosInstance;
  private config: ReconLabSDKConfig;

  /**
   * Create a new ReconLab API client
   * @param config Configuration options
   */
  constructor(config: ReconLabSDKConfig = {}) {
    this.config = {
      baseURL: 'https://reconlab.io', // Replace with actual base URL
      timeout: 30000,
      ...config,
    };

    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for API key authentication
    this.client.interceptors.request.use(
      (config) => {
        if (this.config.apiKey) {
          config.headers['X-API-Key'] = this.config.apiKey;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle common API errors
        if (error.response) {
          const { status, data } = error.response;
          
          // API key errors
          if (status === 401 && (data.code === 'MISSING_API_KEY' || data.code === 'INVALID_API_KEY')) {
            throw new Error(`Authentication error: ${data.error || 'Invalid or missing API key'}`);
          }
          
          // Rate limiting errors
          if (status === 429) {
            throw new Error(`Rate limit exceeded: ${data.error || 'Too many requests'}`);
          }
          
          // Other API errors
          throw new Error(`API error (${status}): ${data.error || 'Unknown error'}`);
        } else if (error.request) {
          // Network errors
          throw new Error('Network error: Unable to connect to the API');
        } else {
          // Other errors
          throw new Error(`Error: ${error.message}`);
        }
      }
    );
  }

  /**
   * Set API key for authentication
   * @param apiKey The API key
   */
  setApiKey(apiKey: string): void {
    this.config.apiKey = apiKey;
  }

  /**
   * Get current API key
   * @returns The current API key
   */
  getApiKey(): string | undefined {
    return this.config.apiKey;
  }

  /**
   * Set wallet address
   * @param walletAddress The wallet address
   */
  setWalletAddress(walletAddress: string): void {
    this.config.walletAddress = walletAddress;
  }

  /**
   * Get current wallet address
   * @returns The current wallet address
   */
  getWalletAddress(): string | undefined {
    return this.config.walletAddress;
  }

  /**
   * Make a GET request
   * @param url The endpoint URL
   * @param params Query parameters
   * @returns Promise resolving to the response data
   */
  async get<T = any>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url, { params });
    return response.data;
  }

  /**
   * Make a POST request
   * @param url The endpoint URL
   * @param data Request body data
   * @param config Additional request configuration
   * @returns Promise resolving to the response data
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data, config);
    return response.data;
  }

  /**
   * Make a PATCH request
   * @param url The endpoint URL
   * @param data Request body data
   * @returns Promise resolving to the response data
   */
  async patch<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.patch(url, data);
    return response.data;
  }

  /**
   * Make a DELETE request
   * @param url The endpoint URL
   * @param config Additional request configuration
   * @returns Promise resolving to the response data
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url, config);
    return response.data;
  }
}
